import { useEffect, useMemo } from "react";
import { Pause, Play } from "lucide-react";

import { useT } from "../../i18n";
import { plural } from "../../i18n/plural";
import { useLocaleStore } from "../../store/localeStore";
import { useFormat } from "../../lib/format";
import { useReducedMotion } from "../../hooks/useReducedMotion";
import { useDataStore } from "../../store/dataStore";
import { useFiltersStore } from "../../store/filtersStore";
import {
  DAY_MS,
  SPEED_OPTIONS,
  WINDOW_OPTIONS,
  useTimelineStore,
} from "../../store/timelineStore";

const parseDay = (iso: string) => Date.parse(`${iso}T00:00:00Z`);
const toIso = (ms: number) => new Date(ms).toISOString().slice(0, 10);

/** Playback loop: advances the cursor while playing (brief §9). */
function usePlayback() {
  const isPlaying = useTimelineStore((s) => s.isPlaying);
  const reduced = useReducedMotion();

  useEffect(() => {
    if (!isPlaying) return;

    // Reduced motion: no animated sweep — jump to the end and stop (§13).
    if (reduced) {
      const { max, setCursor, pause } = useTimelineStore.getState();
      setCursor(max);
      pause();
      return;
    }

    const DAYS_PER_SEC = 10;
    let last = performance.now();
    let raf = 0;
    const tick = (now: number) => {
      const { cursor, max, speed, setCursor, pause } = useTimelineStore.getState();
      const dtSec = (now - last) / 1000;
      last = now;
      const next = cursor + dtSec * DAYS_PER_SEC * speed * DAY_MS;
      if (next >= max) {
        setCursor(max);
        pause();
        return;
      }
      setCursor(next);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [isPlaying, reduced]);
}

export function TimelineBar() {
  const t = useT();
  const fmt = useFormat();
  const locale = useLocaleStore((s) => s.locale);
  const meta = useDataStore((s) => s.meta);
  const records = useDataStore((s) => s.records);
  const appliedFrom = useFiltersStore((s) => s.applied.from);
  const appliedTo = useFiltersStore((s) => s.applied.to);

  const { min, max, cursor, windowDays, isPlaying, speed } = useTimelineStore();
  const setRange = useTimelineStore((s) => s.setRange);
  const setCursor = useTimelineStore((s) => s.setCursor);
  const setWindowDays = useTimelineStore((s) => s.setWindowDays);
  const setSpeed = useTimelineStore((s) => s.setSpeed);
  const togglePlay = useTimelineStore((s) => s.togglePlay);

  usePlayback();

  // Range follows the applied filter period, or the dataset bounds otherwise.
  useEffect(() => {
    if (!meta) return;
    const lo = appliedFrom ? parseDay(appliedFrom) : parseDay(meta.dateRange.min);
    const hi = appliedTo ? parseDay(appliedTo) : parseDay(meta.dateRange.max);
    if (hi > lo) setRange(lo, hi);
  }, [meta, appliedFrom, appliedTo, setRange]);

  // Ticks on acquisition dates that fall inside the range.
  const ticks = useMemo(() => {
    if (!meta || max <= min) return [];
    return meta.dates
      .map((d) => parseDay(d))
      .filter((ms) => ms >= min && ms <= max)
      .map((ms) => ((ms - min) / (max - min)) * 100);
  }, [meta, min, max]);

  // Episodes with a detection inside the current accumulation window.
  const activeCount = useMemo(() => {
    const active = new Set<string>();
    for (const r of records) {
      const ageDays = (cursor - r.dateMs) / DAY_MS;
      if (ageDays >= 0 && ageDays <= windowDays) active.add(r.episodeId);
    }
    return active.size;
  }, [records, cursor, windowDays]);

  const hasRange = max > min;

  return (
    <footer className="flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-border-subtle bg-surface-raised px-4 py-2">
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={togglePlay}
          disabled={!hasRange}
          aria-label={isPlaying ? t.timeline.pause : t.timeline.play}
          title={isPlaying ? t.timeline.pause : t.timeline.play}
          className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-accent text-text-onAccent transition-colors hover:bg-accent-hover disabled:cursor-not-allowed disabled:bg-surface-sunken disabled:text-text-disabled"
        >
          {isPlaying ? <Pause size={16} /> : <Play size={16} />}
        </button>
        <label className="flex items-center gap-1 text-xs text-text-tertiary">
          <span>{t.timeline.speed}</span>
          <select
            value={speed}
            onChange={(e) => setSpeed(Number(e.target.value))}
            className="rounded border border-border-default bg-surface-sunken px-1 py-0.5 text-text-primary"
          >
            {SPEED_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {s}×
              </option>
            ))}
          </select>
        </label>
      </div>

      {/* Track: ticks + cursor. */}
      <div className="relative min-w-[180px] flex-1">
        <div className="pointer-events-none absolute inset-x-0 top-1/2 h-4 -translate-y-1/2">
          {ticks.map((left, i) => (
            <span
              key={i}
              className="absolute top-0 h-3 w-px bg-border-strong"
              style={{ left: `${left}%` }}
            />
          ))}
        </div>
        <input
          type="range"
          min={min}
          max={max}
          step={DAY_MS}
          value={cursor}
          disabled={!hasRange}
          onChange={(e) => setCursor(Number(e.target.value))}
          aria-label={t.timeline.cursor}
          className="relative w-full accent-accent"
        />
        <div className="mt-0.5 flex justify-between text-xs text-text-tertiary">
          <span className="data-value">{hasRange ? fmt.date(toIso(min)) : "—"}</span>
          <span className="data-value">{hasRange ? fmt.date(toIso(max)) : "—"}</span>
        </div>
      </div>

      <label className="flex items-center gap-1 text-xs text-text-tertiary" title={t.timeline.windowHint}>
        <span>{t.timeline.window}</span>
        <select
          value={windowDays}
          onChange={(e) => setWindowDays(Number(e.target.value))}
          className="rounded border border-border-default bg-surface-sunken px-1 py-0.5 text-text-primary"
        >
          {WINDOW_OPTIONS.map((w) => (
            <option key={w} value={w}>
              {w} {t.timeline.windowUnit}
            </option>
          ))}
        </select>
      </label>

      <div className="min-w-[9rem] text-right">
        <div className="data-value text-sm font-medium text-text-primary">
          {hasRange ? fmt.date(toIso(cursor)) : "—"}
        </div>
        <div className="text-xs text-text-tertiary">
          {records.length ? plural(locale, activeCount, t.timeline.activeEpisodes) : t.timeline.noDates}
        </div>
      </div>
    </footer>
  );
}
